import { ComponentBase } from '../components/ComponentBase';
import { template, unsafeHTML } from '../utils/TemplateParser';
import { lang } from '../lang';
import { vars } from '../utils/vars';
import { api } from '../utils/api';
import { loadJavaScript } from '../utils/ResourceLoader';
import { Emitters } from '../utils/Emitters';
import { DateTime } from 'luxon';
import { load as loadCAPTCHA } from 'recaptcha-v3';
import MCDatepicker from '../../mc-datepicker/mc-calendar.min.js';


export class PublicSubmission extends ComponentBase {
    constructor() {
        super({id: 'event-submission-form'});

        this.dateFormat = 'MM/dd/yyyy';
        this.fetch();
    }

    // Templates
    template() {
        const defaultDateTime = this.getDefaultDateTime();
        const defaultDate = defaultDateTime.toFormat(this.dateFormat);
        const defaultHour = defaultDateTime.toFormat('h');
        const defaultAMPM = defaultDateTime.toFormat('a');

        return template`
            <div class="hero">
                <div class="wrapper">
                    <div class="controls">
                        <h1 class="page-title" id="public-submission-page-title">${lang.EventSubmissionHeading}</h1>
                    </div>
                </div>
            </div>
            <div class="wrapper">
                <div role="region" class="page" aria-labelledby="public-submission-page-title">
                    <div class="container">
                        <div id="passcode-wrapper" class="step-wrapper${this.passCodeRequired() ? '' : ' hide'}">
                            <form id="verify-passcode-form" autocomplete="off" novalidate>
                                <fieldset>
                                    <legend>${lang.Verification}</legend>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="passcode">${lang.Passcode}</label>
                                        </div>
                                        <div class="col-75">
                                            <input type="password" id="passcode" name="passcode" aria-describedby="help-block-passcode" required>
                                            <span id="help-block-passcode" class="help-block">${lang.PasscodeHelperText}</span>
                                            ${unsafeHTML(this.errorBlockTemplate('passcode'))}
                                        </div>
                                    </div>
                                    <div class="button-group">
                                        <button type="button" data-action="cancel">${lang.Cancel}</button>
                                        <button type="submit" data-action="verify-passcode" class="submit">${lang.Verify}<div class="loader-ring"><div></div><div></div><div></div><div></div></div></button>
                                    </div>
                                    <div class="general-error-message"></div>
                                    <div class="google-recaptcha-statement">${unsafeHTML(lang.MCPrivacyStatement)} ${unsafeHTML(lang.GoogleRecaptchaStatement)}</div>
                                </fieldset>
                            </form>
                        </div>
                        <div id="public-submission-wrapper" class="step-wrapper${this.passCodeRequired() ? ' hide' : ''}">
                            <div id="instructions">
                                <h3>${lang.Instructions}</h3>
                                <div>${lang.EventSubmissionInstructions}</div>
                                <div>${lang.asteriskFieldsRequired}</div>
                            </div>
                            <form id="public-submission-form" data-action="submit-form" novalidate>
                                <fieldset>
                                    <legend>${lang.Contact}</legend>
                                    <div class="instructions">${lang.publicSubmissionContactInstructions}</div>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="submissionName">${lang.Name} *</label>
                                        </div>
                                        <div class="col-75">
                                            <input type="text" id="submissionName" name="submissionName" aria-describedby="help-block-submissionName" maxlength="128" required>
                                            <span id="help-block-submissionName" class="help-block">${lang.NameHelperText}</span>
                                            ${unsafeHTML(this.errorBlockTemplate('submissionName'))}
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="submissionEmail">${lang.Email} *</label>
                                        </div>
                                        <div class="col-75">
                                            <input type="text" id="submissionEmail" name="submissionEmail" aria-describedby="help-block-submissionEmail" maxlength="255" required>
                                            ${unsafeHTML(this.errorBlockTemplate('submissionEmail'))}
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="submissionPhone">${lang.Phone}</label>
                                        </div>
                                        <div class="col-75">
                                            <input type="text" id="submissionPhone" name="submissionPhone" aria-describedby="help-block-submissionPhone" maxlength="32">
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="submissionType">${lang.IAmA} *</label>
                                        </div>
                                        <div class="col-75">
                                            <select id="submissionType" name="submissionType" required>
                                                <option value=""></option>
                                                ${unsafeHTML(this.getSubmissionTypeOptions())}
                                            </select>
                                            <span id="help-block-submissionType" class="help-block">${lang.submissionTypeHelperText}</span>
                                            ${unsafeHTML(this.errorBlockTemplate('submissionType'))}
                                        </div>
                                    </div>
                                    <div id="form-group-other" class="form-group hide">
                                        <div class="col-25">
                                            <label for="typeOther">${lang.Other} *</label>
                                        </div>
                                        <div class="col-75">
                                            <input type="text" id="typeOther" name="typeOther" placeholder="${unsafeHTML(lang.IAmAPlaceholder)}" maxlength="32" required>
                                            <span id="help-block-typeOther" class="help-block">${lang.typeOtherHelperText}</span>
                                            ${unsafeHTML(this.errorBlockTemplate('typeOther'))}
                                        </div>
                                    </div>
                                </fieldset>
                                <fieldset>
                                    <legend>${lang.EventDetails}</legend>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="category">${lang.PrimaryCalendar} *</label>
                                        </div>
                                        <div class="col-75">
                                            <select id="category" name="category" required>
                                                <option value=""></option>
                                                ${unsafeHTML(this.getCategoryOptions())}
                                            </select>
                                            <span id="help-block-category" class="help-block">${lang.categoryHelperText}</span>
                                            ${unsafeHTML(this.errorBlockTemplate('category'))}
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="title">${lang.EventTitle} *</label>
                                        </div>
                                        <div class="col-75">
                                            <input type="text" id="title" name="title" maxlength="255" required>
                                            ${unsafeHTML(this.errorBlockTemplate('title'))}
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="description">${lang.EventDescription}</label>
                                        </div>
                                        <div class="col-75">
                                            <textarea id="description" name="description"></textarea>
                                        </div>
                                    </div>
                                    <div class="separator"></div>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="from">${lang.From} *</label>
                                        </div>
                                        <div class="col-75">
                                            <div class="date-time-fields">
                                                <div class="input-button-group">
                                                    <input type="text" id="from" name="from" value="${defaultDate}" readonly required>
                                                    <button type="button" data-action="toggle-datepicker"><i class="icon fas fa-calendar-alt"></i></button>
                                                </div>
                                                <div class="time-fields">
                                                    <select id="fromHour" name="fromHour">${unsafeHTML(this.getHourOptions(defaultHour))}</select>
                                                    <select id="fromMinute" name="fromMinute">${unsafeHTML(this.getMinuteOptions('00'))}</select>
                                                    <select id="fromAMPM" name="fromAMPM">${unsafeHTML(this.getAMPMOptions(defaultAMPM))}</select>
                                                    <span>${vars.pubcalendar.timezoneLabel}</span>
                                                </div>
                                            </div>
                                            ${unsafeHTML(this.errorBlockTemplate('from'))}
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="to">${lang.To} *</label>
                                        </div>
                                        <div class="col-75">
                                            <div class="date-time-fields">
                                                <div class="input-button-group">
                                                    <input type="text" id="to" name="to" value="${defaultDate}" readonly required>
                                                    <button type="button" data-action="toggle-datepicker"><i class="icon fas fa-calendar-alt"></i></button>
                                                </div>
                                                <div class="time-fields">
                                                    <select id="toHour" name="toHour">${unsafeHTML(this.getHourOptions(defaultHour))}</select>
                                                    <select id="toMinute" name="toMinute">${unsafeHTML(this.getMinuteOptions('30'))}</select>
                                                    <select id="toAMPM" name="toAMPM">${unsafeHTML(this.getAMPMOptions(defaultAMPM))}</select>
                                                    <span>${vars.pubcalendar.timezoneLabel}</span>
                                                </div>
                                            </div>
                                            ${unsafeHTML(this.errorBlockTemplate('to'))}
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <div class="col-25"></div>
                                        <div class="col-75">
                                            <label><input type="checkbox" id="allDay" name="allDay" value="true"><span> ${lang.AllDayEvent}</span></label>
                                        </div>
                                    </div>
                                    <div class="separator"></div>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="tags">${lang.SuggestedCategories}</label>
                                        </div>
                                        <div class="col-75">
                                            <input type="text" id="tags" name="tags" aria-describedby="help-block-tags">
                                            <span id="help-block-tags" class="help-block">${lang.SuggestedCategoriesHelperText}</span>
                                            ${unsafeHTML(this.errorBlockTemplate('tags'))}
                                        </div>
                                    </div>
                                </fieldset>
                                <fieldset>
                                    <legend>${lang.EventLocation}</legend>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="locationName">${lang.LocationName}</label>
                                        </div>
                                        <div class="col-75">
                                            <input type="text" id="locationName" name="locationName" maxlength="100">
                                            ${unsafeHTML(this.errorBlockTemplate('locationName'))}
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="locationAddress">${lang.PhysicalAddress}</label>
                                        </div>
                                        <div class="col-75">
                                            <input type="text" id="locationAddress" name="locationAddress" placeholder="" maxlength="100">
                                            ${unsafeHTML(this.errorBlockTemplate('locationAddress'))}
                                        </div>
                                    </div>
                                    <div class="separator"></div>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="conferenceLink">${lang.WebConferenceLink}</label>
                                        </div>
                                        <div class="col-75">
                                            <input type="text" id="conferenceLink" name="conferenceLink" maxlength="255" placeholder="${lang.sampleURL}">
                                            ${unsafeHTML(this.errorBlockTemplate('conferenceLink'))}
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="conferenceButtonLabel">${lang.ButtonLabel}</label>
                                        </div>
                                        <div class="col-75">
                                            <input type="text" id="conferenceButtonLabel" name="conferenceButtonLabel" aria-describedby="help-block-conferenceButtonLabel" maxlength="32">
                                            <span id="help-block-conferenceButtonLabel" class="help-block">${lang.conferenceButtonLabelHelperText}</span>
                                            ${unsafeHTML(this.errorBlockTemplate('conferenceButtonLabel'))}
                                        </div>
                                    </div>
                                </fieldset>
                                <fieldset>
                                    <legend>${lang.EventOrganizer}</legend>
                                    <div class="form-group">
                                        <div class="col-25"></div>
                                        <div class="col-75">
                                        <label><input type="checkbox" id="sameAsSubmitter" name="sameAsSubmitter"><span>${lang.SameAsSubmitter}</span></label>
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="organizerName">${lang.OrganizerName}</label>
                                        </div>
                                        <div class="col-75">
                                            <input type="text" id="organizerName" name="organizerName" maxlength="100">
                                            <span id="help-block-organizerName" class="help-block">${lang.organizerNameHelperText}</span>
                                            ${unsafeHTML(this.errorBlockTemplate('organizerName'))}
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="organizerEmail">${lang.OrganizerEmail}</label>
                                        </div>
                                        <div class="col-75">
                                            <input type="text" id="organizerEmail" name="organizerEmail" maxlength="255">
                                            ${unsafeHTML(this.errorBlockTemplate('organizerEmail'))}
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="organizerPhone">${lang.OrganizerPhone}</label>
                                        </div>
                                        <div class="col-75">
                                            <input type="text" id="organizerPhone" name="organizerPhone" maxlength="32">
                                            ${unsafeHTML(this.errorBlockTemplate('organizerPhone'))}
                                        </div>
                                    </div>
                                </fieldset>
                                <fieldset>
                                    <legend>${lang.TicketsAndCost}</legend>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="ticketOption">${lang.EventCost}</label>
                                        </div>
                                        <div class="col-75">
                                            <select id="ticketOption" name="ticketOption">
                                                ${unsafeHTML(this.getTicketOptions())}
                                            </select>
                                            <span id="help-block-ticketOption" class="help-block">${lang.ticketOptionHelperText}</span>
                                            ${unsafeHTML(this.errorBlockTemplate('ticketOption'))}
                                        </div>
                                    </div>
                                    <div id="form-group-price" class="form-group hide">
                                        <div class="col-25">
                                            <label for="ticketPriceMinimum">${lang.Price}</label>
                                        </div>
                                        <div class="col-75">
                                            <div class="event-price-fields">
                                                <input type="number" id="ticketPriceMinimum" name="ticketPriceMinimum" min="0">
                                                <div id="form-group-price-maximum">
                                                    <span>${lang.to}</span>
                                                    <input type="number" id="ticketPriceMaximum" name="ticketPriceMaximum" min="0" aria-label="${lang.EndOfPriceRange}">
                                                </div>
                                            </div>
                                            ${unsafeHTML(this.errorBlockTemplate('ticketPriceMinimum'))}
                                        </div>
                                    </div>
                                    <div id="form-group-currency" class="form-group hide">
                                        <div class="col-25">
                                            <label for="ticketCurrency">${lang.EventCurrency}</label>
                                        </div>
                                        <div class="col-75">
                                            <select id="ticketCurrency" name="ticketCurrency">
                                            ${unsafeHTML(this.getTicketCurrencyOptions())}
                                            </select>
                                            ${unsafeHTML(this.errorBlockTemplate('ticketCurrency'))}
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="ticketUrl">${lang.TicketLink}</label>
                                        </div>
                                        <div class="col-75">
                                            <input type="text" id="ticketUrl" name="ticketUrl" maxlength="255" placeholder="${lang.sampleURL}">
                                            ${unsafeHTML(this.errorBlockTemplate('ticketUrl'))}
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="ticketButtonLabel">${lang.ButtonLabel}</label>
                                        </div>
                                        <div class="col-75">
                                            <input type="text" id="ticketButtonLabel" name="ticketButtonLabel" aria-describedby="help-block-ticketButtonLabel" maxlength="32">
                                            <span id="help-block-ticketButtonLabel" class="help-block">${lang.ticketButtonLabelHelperText}</span>
                                            ${unsafeHTML(this.errorBlockTemplate('ticketButtonLabel'))}
                                        </div>
                                    </div>
                                    </fieldset>
                                    <fieldset>
                                    <legend>${lang.CommentsToAdministrator}</legend>
                                    <div class="form-group">
                                        <div class="col-25">
                                            <label for="submissionAbout">${lang.Comments}</label>
                                        </div>
                                        <div class="col-75">
                                            <textarea id="submissionAbout" name="submissionAbout" aria-describedby="help-block-submissionAbout"></textarea>
                                            <span id="help-block-submissionAbout" class="help-block">${lang.CommentsHelperText}</span>
                                        </div>
                                    </div>
                                    <div class="button-group">
                                        <button type="button" data-action="cancel">${lang.Cancel}</button>
                                        <button type="submit" data-action="submit" class="submit">${lang.SubmitEvent}<div class="loader-ring"><div></div><div></div><div></div><div></div></div></button>
                                    </div>
                                    <div class="general-error-message"></div>
                                    <div class="google-recaptcha-statement">${unsafeHTML(lang.MCPrivacyStatement)} ${unsafeHTML(lang.GoogleRecaptchaStatement)}</div>
                                </fieldset>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    formSubmittedTemplate() {
        return template`
            <div class="form-submitted-message">
                <h3>${lang.EventSubmissionFormSubmittedHeading}</h3>
                <p>${lang.EventSubmissionFormSubmittedMessage}</p>
                <a href="${this.getRouteURL('')}" data-action="routeChange">${lang.ReturnToCalendar}</a>
            </div>
        `;
    }

    errorBlockTemplate(id) {
        return `<span id="error-block-${id}" class="error-block"><i class="icon fas fa-info-circle"></i> <span></span></span>`;
    }

    // ComponentBase methods
    fetch() {
        api.getCategories()
        .then(data => {
            this.categories = data;
            this.render();
        })
        .catch((error) => {
            // redirect to calendar landing page
            this.triggerRouteChange(this.getRouteURL(''));
        });
    }

    render() {
        if(!vars.pubcalendar.submit) {
            // redirect to calendar landing page since public submissions is disabled
            this.triggerRouteChange(this.getRouteURL(''));
            return;
        }
        super.render();
    }

    afterRender() {
        api.setPageTitle(lang.EventSubmission);
        api.setPageDescription(lang.EventSubmissionDescription);
        api.setPageCanonical(document.location.href);
        this.initDatepickers();
        this.initGoogleAutocomplete();
        this.el.querySelector('.step-wrapper:not(.hide) input').focus(); // focus first visible input field
    }

    domReady() {
        this.elements = {
            form: this.el.querySelector('#public-submission-form'),
            passcodeForm: this.el.querySelector('#verify-passcode-form'),
            submitButton: this.el.querySelector('[data-action="submit"]'),
            verifyButton: this.el.querySelector('[data-action="verify-passcode"]'),
            fromDate: this.el.querySelector('#from'),
            toDate: this.el.querySelector('#to'),
            formGroupOther: this.el.querySelector('#form-group-other'),
            formGroupPrice: this.el.querySelector('#form-group-price'),
            formGroupPriceMaximum: this.el.querySelector('#form-group-price-maximum'),
            formGroupCurrency: this.el.querySelector('#form-group-currency'),
            sameAsSubmitter: this.el.querySelector('#sameAsSubmitter'),
            submissionName: this.el.querySelector('#submissionName'),
            submissionEmail: this.el.querySelector('#submissionEmail'),
            submissionPhone: this.el.querySelector('#submissionPhone'),
            organizerName: this.el.querySelector('#organizerName'),
            organizerEmail: this.el.querySelector('#organizerEmail'),
            organizerPhone: this.el.querySelector('#organizerPhone'),
            
        };

        Emitters.dispatchConvertIcons(this.el);
        Emitters.dispatchViewLoaded();
    }

    registerEvents() {
        return {
            'click #allDay'                                             : 'toggleAllDay',
            'change #submissionType'                                    : 'handelTypeChanged',
            'change #ticketOption'                                      : 'handelTicketOptionChanged',
            'click [data-action="cancel"]'                              : 'cancelForm',
            'click [data-action="submit"]'                              : 'submitForm',
            'click [data-action="verify-passcode"]'                     : 'verifyPasscode',
            'click [data-action="toggle-datepicker"]'                   : 'toggleDatepicker',
            'keypress #public-submission-form'                          : 'keyboard',
            'click'                                                     : 'handleClickOutsideDatepicker',
            'change select'                                             : 'clearError',
            'keydown input, textarea'                                   : 'clearError',
            'click a[data-action="routeChange"]'                        : 'handleRouteChange',
            'click #sameAsSubmitter'                                    : 'handleSameAsSubmitter',
            'keyup #submissionName, #submissionEmail, #submissionPhone' : 'handleSameAsSubmitter',
        };
    }

    /* Event Handlers */
    focusTrap(evt, firstElement, lastElement) {
        // trap focus so that tabbing is limited to cycling through elements between the first and last elements specified
        const activeElement = vars.calendarShadowDom.activeElement;

        if(evt.shiftKey) { /* shift + tab */
            if (activeElement === firstElement) {
                lastElement.focus();
                evt.preventDefault();
            }
        } else { /* tab */
            if (activeElement === lastElement) {
                firstElement.focus();
                evt.preventDefault();
            }
        }
    }

    toggleAllDay(e) {
        this.disableTime(e.target.checked);
    }

    handelTypeChanged(e) {
        this.showTypeOther(e.target.value == lang.eventSubmissionTypes.Other);
    }

    handelTicketOptionChanged(e) {
        this.clearError(this.el.querySelector('#form-group-price'));
        this.showPrice(e.target.value.includes('PRICE'), e.target.value == 'PRICE_RANGE');
    }

    cancelForm() {
        // redirect to calendar landing page
        this.triggerRouteChange(this.getRouteURL(''));
    }

    submitForm(e) {
        e.preventDefault();
        
        const data = Object.fromEntries(new FormData(this.elements.form));

        this.setDateTimeProperties(data);

        if(!this.validate(data)) return;

        this.purgeProperties(data);

        this.elements.submitButton.disabled = true;

        const options = {
            useRecaptchaNet: true,
            autoHideBadge: true,
        };

        loadCAPTCHA(vars.pubcalendar.captchaSiteKey, options).then(recaptcha => {
            recaptcha.execute('public_submission').then(token => {
                data.token = token;
                if(this.passcode) data.passcode = this.passcode;

                api.submitEvent(data)
                .then(() => this.displayFormSubmittedMessage())
                .catch(response => {
                    this.elements.submitButton.disabled = false;
                    if(!(response instanceof Error) && response instanceof Object) {
                        this.displayInlineErrors(response);
                    } else {
                        this.setGeneralError(response || lang.GenericError);
                    }
                });

            });
        });
    }

    verifyPasscode(e) {
        e.preventDefault();
        
        const data = Object.fromEntries(new FormData(this.elements.passcodeForm));

        if(!this.validatePasscode(data)) return;

        this.elements.verifyButton.disabled = true;

        const options = {
            useRecaptchaNet: true,
            autoHideBadge: true,
        };

        loadCAPTCHA(vars.pubcalendar.captchaSiteKey, options).then(recaptcha => {
            recaptcha.execute('passcode_verification').then(token => {
                data.token = token;

                api.verifyPasscode(data)
                .then(() => {
                    this.passcode = data.passcode;
                    this.displayForm();
                })
                .catch(response => {
                    this.elements.verifyButton.disabled = false;
                    if(!(response instanceof Error) && response instanceof Object) {
                        this.displayInlineErrors(response);
                    } else {
                        this.setGeneralError(response || lang.GenericError);
                    }
                });

            });
        });
    }

    displayForm() {
        this.el.querySelector('#passcode-wrapper').classList.add('hide');
        this.el.querySelector('#public-submission-wrapper').classList.remove('hide');
    }

    passCodeRequired() {
        return vars.pubcalendar.hasSubmitPasscode;
    }

    toggleDatepicker(e) {
        const datePickerField = e.target.closest('.input-button-group').querySelector('input[type="text"]');
        const datePicker = datePickerField.id == 'from' ? this.datePickerFrom : this.datePickerTo;

        if(this.isDatepickerOpen() && this.returnFocusEl == datePicker.linkedElement) {
            datePicker.close();
            datePicker.linkedElement.focus();
        } else {
            datePicker.open();
        }
    }

    handleSameAsSubmitter() {
        if(this.elements.sameAsSubmitter.checked) {
            ['Name','Email','Phone'].forEach(i => this.elements[`organizer${i}`].value = this.elements[`submission${i}`].value);
        }
    }

    keyboard(e) {
        const target = e.target;
   
        if (e.key == 'Enter') {
            if(target.tagName == 'INPUT') e.preventDefault(); // prevents form from being submitted when the enter key is pressed within a text input field
            if(target.id == 'from') this.datePickerFrom.open();
            if(target.id == 'to') this.datePickerTo.open();
        }
    }

    handleClickOutsideDatepicker(e) {
        // automatically close datepicker
        if(this.isDatepickerOpen() && !e.target.closest('.input-button-group')) {
            this.returnFocusEl = null; // clear return focus
            this.datePickerFrom.close();
            this.datePickerTo.close();
        }
    }

    clearError(e) {
        const target = e instanceof Element ? e : e.target.closest('.form-group'); // the 'e' argument can be an element or an event
        target.classList.remove('error');
    }

    // Utility methods
    validate(data){
        const errors = {};
        const emailPattern = /^[^@]+@[^@]+\.[^@.]+$/;

        this.trimData(data);

        if(!data.submissionName) errors.submissionName = lang.submissionNameValidationMessage;
        if(!data.category) errors.category = lang.categoryValidationMessage;
        if(!data.title) errors.title = lang.titleValidationMessage;

        if(!data.submissionType) errors.submissionType = lang.submissionTypeValidationMessage;
        
        if(data.submissionType == lang.eventSubmissionTypes.Other) {
            data.submissionType = data.typeOther;
            if(!data.submissionType) errors.typeOther = lang.typeOtherValidationMessage;
        }

        if(!emailPattern.test(data.submissionEmail)){ // required, validate email
            errors.submissionEmail = lang.submissionEmailValidationMessage;
        }

        if(data.organizerEmail && !emailPattern.test(data.organizerEmail)){ // optional, but validate email when entered
            errors.organizerEmail = lang.organizerEmailValidationMessage;
        }

        if(data.allDay) {
            if(data.startDateObj > data.endDateObj) errors.to = lang.toDateValidationMessage;
        } else {
            if(data.startDatetimeObj > data.endDatetimeObj) errors.to = lang.toDatetimeValidationMessage;
        }

        data.tags = data.tags ? data.tags.split(',').map(tag => tag.trim().replace(/\s+/g, ' ')) : [];
        data.tags.every(tag => {
            const unallowed = /[\$\^\|,+#\/;\\\?]/;
            if(tag.length > 128 || unallowed.test(tag)) {
                errors.tags = lang.categoriesValidationMessage;
                return false; // break the 'every' loop as soon as an invalid tag is found.
            }
            return true;
        });

        if(data.ticketOption == 'ONE_PRICE') {
            data.ticketPriceMaximum = null;
            if(!this.validatePrice(data.ticketPriceMinimum)) errors.ticketPriceMinimum = lang.eventPriceValidationMessage;
        } else if(data.ticketOption == 'PRICE_RANGE') {
            if(!this.validatePriceRange(data)) errors.ticketPriceMinimum = lang.eventPriceRangeValidationMessage;
        } else {
            data.ticketPriceMinimum = null;
            data.ticketPriceMaximum = null;
        }

        this.displayInlineErrors(errors);

        return !Object.keys(errors).length; // If no errors exist return true (valid)
    }

    validatePrice(price) {
        // number less than 1 billion with optional one or two decimal places
        return /^[1-9]\d{0,8}(\.\d{1,2})?$|^0(\.\d{1,2})$/.test(price);
    }

    validatePriceRange({ticketPriceMinimum, ticketPriceMaximum}) {
        return this.validatePrice(ticketPriceMinimum) && this.validatePrice(ticketPriceMaximum) && parseFloat(ticketPriceMinimum) < parseFloat(ticketPriceMaximum);
    }

    validatePasscode(data) {
        const errors = {};

        this.trimData(data);

        if(!data.passcode || data.passcode.includes(' ') || data.passcode.length < 6 || data.passcode.length > 10) errors.passcode = lang.passcodeInvalid;

        this.displayInlineErrors(errors);

        return !Object.keys(errors).length; // If no errors exist return true (valid)
    }

    getDefaultDateTime() {
        return DateTime.now().plus({hours: 2}).setZone(vars.pubcalendar.timezone);
    }

    setDateTimeProperties(data) {
        data.allDay = this.el.querySelector('#allDay').checked;

        if(data.allDay) {
            data.startDateObj = DateTime.fromFormat(data.from, this.dateFormat, {zone: 'UTC'});
            data.endDateObj = DateTime.fromFormat(data.to, this.dateFormat, {zone: 'UTC'});
            data.startDate = data.startDateObj.toISODate();
            data.endDate = data.endDateObj.toISODate();
        } else {
            data.startDatetimeObj = DateTime.fromFormat(`${data.from} ${data.fromHour}:${data.fromMinute}${data.fromAMPM}`, `${this.dateFormat} h:mma`, {zone: vars.pubcalendar.timezone});
            data.endDatetimeObj = DateTime.fromFormat(`${data.to} ${data.toHour}:${data.toMinute}${data.toAMPM}`, `${this.dateFormat} h:mma`, {zone: vars.pubcalendar.timezone});
            data.startDatetime = data.startDatetimeObj.toUTC().toISO().split('.')[0];
            data.endDatetime = data.endDatetimeObj.toUTC().toISO().split('.')[0];
        }
    }

    disableTime(disable) {
        this.el.querySelectorAll('#fromHour, #fromMinute, #fromAMPM, #toHour, #toMinute, #toAMPM').forEach(el => el.disabled = disable);
    }

    showTypeOther(show = true) {
        if(show) {
            this.elements.formGroupOther.classList.remove('hide');
        } else {
            this.elements.formGroupOther.classList.add('hide');
        }
    }

    showPrice(show, show_price_maximum) {
        if(show_price_maximum) {
            this.elements.formGroupPriceMaximum.classList.remove('hide');
        } else {
            this.elements.formGroupPriceMaximum.classList.add('hide');
        }

        if(show) {
            this.elements.formGroupPrice.classList.remove('hide');
            this.elements.formGroupCurrency.classList.remove('hide');
        } else {
            this.elements.formGroupPrice.classList.add('hide');
            this.elements.formGroupCurrency.classList.add('hide');
        }
    }

    purgeProperties(data) {
        // these properties were used on the front end only and do not need to be included in the final payload
        [
            'from',
            'fromHour',
            'fromMinute',
            'fromAMPM',
            'to',
            'toHour',
            'toMinute',
            'toAMPM',
            'typeOther',
            'allDay',
            'startDateObj',
            'endDateObj',
            'startDatetimeObj',
            'endDatetimeObj',
            'sameAsSubmitter',
        ].forEach(i => delete data[i]);
    }

    displayFormSubmittedMessage() {
        this.renderInto(this.formSubmittedTemplate(), this.el.querySelector('.container'));
    }

    displayInlineErrors(errors) {
        this.clearErrors();

        for(const [field, message] of Object.entries(errors)) {
            const formGroup = this.el.querySelector(`#${field}`).closest('.form-group');
            formGroup.classList.add('error');
            formGroup.querySelector('.error-block span').innerText = message;
        }
        this.focusFirstError();
    }

    setGeneralError(message = '') {
        this.el.querySelector('.step-wrapper:not(.hide) .general-error-message').innerText = message;
    }

    clearErrors() {
        this.el.querySelectorAll('.form-group.error').forEach(elem => this.clearError(elem));
        this.setGeneralError();
    }

    focusFirstError() {
        this.el.querySelector('.form-group.error')?.querySelector('input, select, textarea')?.focus();
    }

    trimData(obj) {
        Object.keys(obj).forEach(k => {
            if(typeof obj[k] === 'string') {
                obj[k] = obj[k].trim();
                if(obj[k] === '') obj[k] = null; // backend requires optional fields to be null instead of empty string
            };
        });
    }

    getSubmissionTypeOptions() {
        const markup = [];

        [
            lang.eventSubmissionTypes.Student,
            lang.eventSubmissionTypes.FacultyMember,
            lang.eventSubmissionTypes.StaffMember,
            lang.eventSubmissionTypes.CommunityMember,
            lang.eventSubmissionTypes.Other,
        ].forEach(t => {
            markup.push(`<option value="${t}">${t}</option>`);
        });

        return markup.join('');
    }

    getTicketOptions() {
        const markup = [];

        [
            '',
            'FREE',
            'ONE_PRICE',
            'PRICE_RANGE',
        ].forEach(i => {
            markup.push(`<option value="${i}">${lang.ticketOptions[i]}</option>`);
        });

        return markup.join('');
    }
    
    getTicketCurrencyOptions() {
        const markup = [];

        [
            'USD',
            'CAD',
            'EUR',
            'GBP',
        ].forEach(i => {
            markup.push(`<option value="${i}">${lang.ticketCurrencyOptions[i]}</option>`);
        });

        return markup.join('');
    }

    getCategoryOptions() {
        const markup = [];
        
        this.categories.forEach(c => {
            markup.push(`<option value="${c.id}">${c.name}</option>`);
        });

        return markup.join('');
    }

    getHourOptions(defaultValue = '') {
        const markup = [];
        const hours = Array(12).fill().map((item, index) => (++index).toString()); // 1 through 12
        hours.unshift(hours.pop()); // moves 12 to beginning

        hours.forEach(h => {
            markup.push(`<option value="${h}"${defaultValue == h ? ' selected' : ''}>${h}</option>`);
        });

        return markup.join('');
    }

    getMinuteOptions(defaultValue = '') {
        const markup = [];

        ['00','15','30','45'].forEach(m => {
            markup.push(`<option value="${m}"${defaultValue == m ? ' selected' : ''}>${m}</option>`);
        });

        return markup.join('');
    }

    getAMPMOptions(defaultValue = '') {
        const markup = [];

        ['AM','PM'].forEach(i => {
            markup.push(`<option value="${i}"${defaultValue == i ? ' selected' : ''}>${i}</option>`);
        });

        return markup.join('');
    }

    adjustDates(el) {
        // automatically adjusts dates so the "from date" is never after the "to date" and vice versa
        const fromDate = DateTime.fromFormat(this.elements.fromDate.value, this.dateFormat);
        const toDate = DateTime.fromFormat(this.elements.toDate.value, this.dateFormat);
        
        if(fromDate <= toDate) return;

        if(el.id == 'to') {
            this.elements.fromDate.value = this.elements.toDate.value;
            this.datePickerFrom.setFullDate(toDate.toJSDate());
        } else {
            this.elements.toDate.value = this.elements.fromDate.value;
            this.datePickerTo.setFullDate(fromDate.toJSDate());
        }
    }

    isDatepickerOpen() {
        return !!vars.calendarShadowDom.querySelector('.mc-calendar.mc-calendar--opened');
    }

    initDatepickers() {
        const datePickerConfig = {
            context: vars.calendarShadowDom.querySelector('#omnicms-calendar-wrapper'),
            bodyType: 'inline',
            dateFormat: this.dateFormat,
            autoClose: true,
            closeOnBlur: true,
            selectedDate: this.getDefaultDateTime().toJSDate(), // default date
            customOkBTN: '',
            customClearBTN: '',
        };

        this.datePickerFrom = MCDatepicker.create({el: '#from', ...datePickerConfig});
        this.datePickerTo  = MCDatepicker.create({el: '#to', ...datePickerConfig});

        const datePicker = vars.calendarShadowDom.querySelector('.mc-calendar'); // all datepickers share the same calendar element
        const datePickerOnOpen = (id) => {
            const dateInput = this.el.querySelector(`#${id}`);
            const inputPosition = dateInput.getBoundingClientRect();
            this.returnFocusEl = dateInput;
            // Always reset vertical position to account for shadow dom
            datePicker.style.top = `${inputPosition.top + inputPosition.height - vars.calendarElement.getBoundingClientRect().top}px`;
            datePicker.querySelector('[tabindex="0"]').focus();
        };

        // Setting datepicker event listeners
        
        // remove default 'open onfocus' behavior
        this.datePickerFrom.linkedElement.onfocus = null;
        this.datePickerTo.linkedElement.onfocus = null;

        this.datePickerFrom.linkedElement.onclick = () => this.datePickerFrom.open();
        this.datePickerTo.linkedElement.onclick = () => this.datePickerTo.open();

        this.datePickerFrom.onOpen(() => datePickerOnOpen('from'));
        this.datePickerTo.onOpen(() => datePickerOnOpen('to'));

        this.datePickerFrom.onSelect(() => this.adjustDates(this.datePickerFrom.linkedElement));
        this.datePickerTo.onSelect(() => this.adjustDates(this.datePickerTo.linkedElement));

        this.datePickerFrom.onClose(() => this.returnFocusEl?.focus());
        this.datePickerTo.onClose(() => this.returnFocusEl?.focus());

        datePicker.addEventListener('keydown', e => this.focusTrap(e, datePicker.querySelector('#mc-picker__month--prev'), datePicker.querySelector('#mc-btn__cancel')));
    }

    initGoogleAutocomplete() {
		if(!vars.pubcalendar.googleMapKey?.length) return; // if googleMapKey is not provided just return without enabling location autocomplete
		
        const locationAddressEl = vars.calendarShadowDom.querySelector('#locationAddress');
        const apiKey = vars.pubcalendar.googleMapKey;

        window.gm_authFailure = () => {
            /*
                This global function is called by the Google Maps API whenever an error occurs.
                The following makes the location autocomplete feature handle errors passively.
                In other words, if location autocomplete is configured correctly on the provided apiKey
                it will work, if not the location address field will fallback to a standard text input field.
            */

            locationAddressEl.disabled = false;
            locationAddressEl.setAttribute('placeholder', '');
            locationAddressEl.removeAttribute('style');
            locationAddressEl.removeAttribute('class');
            locationAddressEl.focus();
        }
		
		loadJavaScript(`https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`).then(() => {
			new google.maps.places.Autocomplete(
				locationAddressEl,
				{ fields: ['formatted_address'] }
			);
		});
	}

}